// 真實後端 Spin 服務 — 透過 GameServerClient 送 SpinCmd、收 SlotResult，再轉為本專案 SpinResponse。
// 兩層 Any 封裝：送出的 ClientSpinCmd.SpinData 與回應的 ResponseInfo.Data 都是 google.protobuf.Any。
//
// ⚠️ 依還原 proto（proto/SlotDef.proto）與推測欄位語意實作，尚未對真後端驗證：
//   - gameCmd（送 spin 的指令字串＝遊戲代碼，非 "Spin"）與 SlotResult 欄位語意需以真後端校正；
//   - SlotResult → SpinResponse 的重建假設見 slotResultMapper.ts 檔頭 [ASSUMED]。
import * as protobuf from 'protobufjs'
import { logger } from '../../framework/core/Logger'
import { toClientBalance, toServerBalance } from '../../framework/net/balance'
import type { GameServerClient } from '../../framework/net/GameServerClient'
import { gatewayType } from '../../framework/net/proto/gatewayProto'
import { ServerError } from '../../framework/net/ServerError'
import { anyType, SPIN_DATA_TYPE_URL, slotResultType, spinCmdType } from '../../framework/net/proto/slotProto'
import { BUY_BONUS_MULTIPLIER, BUY_TYPE_FREE_SPINS } from '../constants'
import { mapSlotResultToSpinResponse, type RawSlotResult } from './slotResultMapper'
import type { SpinRequest, SpinResponse, SpinService } from './SpinService'

const CONVERSION: protobuf.IConversionOptions = { longs: String, defaults: true, arrays: true }

/** Slot026 送 spin 用的預設指令字串（＝遊戲代碼）。⚠️ 實際代碼待後端確認，可經建構子覆寫。 */
const DEFAULT_GAME_CMD = 'Slot026'

export class RemoteSpinService implements SpinService {
  private readonly client: GameServerClient
  private readonly gameCmd: string

  constructor(client: GameServerClient, gameCmd: string = DEFAULT_GAME_CMD) {
    this.client = client
    this.gameCmd = gameCmd
  }

  async spin(request: SpinRequest): Promise<SpinResponse> {
    // 送後端的實際投注額（BaseSpinCmd.TotalBet）：一般局＝基礎注；購買免費遊戲（BuyType=2）＝基礎注×100。
    // ⚠️ 後端對 BuyType=2 會驗證投注額必須為「乘算後的購買花費」，若只送基礎注會被打回 code=13
    //    （InvalidBetAmount，已對 dev 後端實機重現）。此倍率對應 PayTableData.asset BuyType2→Ratio100
    //    （見 constants BUY_BONUS_MULTIPLIER）。SpinRequest.totalBet 維持「基礎注」語意（供 Mock 贏分基準
    //    與內層 SpinCmd），僅在此對接後端信封時換算為實際投注額。
    const wager =
      request.buyType === BUY_TYPE_FREE_SPINS ? request.totalBet * BUY_BONUS_MULTIPLIER : request.totalBet

    // 遊戲層 SpinCmd（包進 Any）。TotalBet/BuyType 依還原 proto 仍存在，但實際多由外層 BaseSpinCmd 帶入
    // （見 docs/PROTOBUF_SCHEMA.md §1 註解）；此處一併帶入避免遺漏，DebugCmdList 留空（一般局）。
    const spinType = spinCmdType()
    const spinValue = spinType
      .encode(spinType.create({ TotalBet: toServerBalance(wager), BuyType: request.buyType }))
      .finish()

    const clientSpinType = gatewayType('ClientSpinCmd')
    const payload = clientSpinType
      .encode(
        clientSpinType.create({
          BaseSpinCmd: {
            TotalBet: toServerBalance(wager),
            BuyType: request.buyType,
            Currency: this.client.currency,
            AutoSpinId: 'Null',
            Turbo: 0,
            IsAuto: false,
            RefFreeVoucherId: '',
          },
          SpinData: { type_url: SPIN_DATA_TYPE_URL, value: spinValue },
        }),
      )
      .finish()

    const response = await this.client.send(this.gameCmd, payload)
    if (response.code !== 0) {
      // 帶 code 往上拋，讓遊戲層照 Unity BaseGameService 的作法顯示 Error_Code_{code} 對話框。
      throw new ServerError(response.code, response.message)
    }

    // ResponseInfo.Data 是 google.protobuf.Any（type_url + value）；先拆 Any 取 value 再解遊戲 SlotResult。
    const anyObj = anyType().toObject(anyType().decode(response.data), { bytes: Uint8Array }) as {
      type_url?: string
      value?: Uint8Array
    }
    const slotBytes = anyObj.value ?? new Uint8Array(0)

    const resultType = slotResultType()
    let raw: RawSlotResult
    try {
      raw = resultType.toObject(resultType.decode(slotBytes), CONVERSION) as RawSlotResult
    } catch (error) {
      const head = Array.from(slotBytes.slice(0, 80))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join(' ')
      logger.error(
        `SlotResult 解碼失敗（type_url=${anyObj.type_url ?? ''} len=${slotBytes.length}）首 80 bytes: ${head}`,
        error,
      )
      throw error
    }

    const mapped = mapSlotResultToSpinResponse(raw)
    // 帶入 envelope 的伺服器權威 spin 後餘額（ResponseInfo.Balance）＋交易 ID（ResponseInfo.TxnId，欄位 5），
    // 供 GameController 校正餘額顯示與右下角狀態列交易 ID（對照 Unity OnSpin 讀 responseInfo.TxnId）。
    return { ...mapped, serverBalance: toClientBalance(response.balance), txnId: response.txnId }
  }
}
